import { useState } from 'react';
import { botApi, logsApi, bannersApi } from '../../../lib/api';
import { toast } from 'sonner';

export const useContentActions = ({ isItalian }) => {
  // Bot phrases
  const [botPhrases, setBotPhrases] = useState([]);
  const [showAddPhraseDialog, setShowAddPhraseDialog] = useState(false);
  const [newPhrase, setNewPhrase] = useState({ command: '!ciao', phrase: '' });
  const [loadingPhrases, setLoadingPhrases] = useState(false);

  // Activity logs
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [actionTypes, setActionTypes] = useState([]);

  // Banners
  const [banners, setBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [showBannerDialog, setShowBannerDialog] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: '', message: '', html_content: '', link: '', image_url: '', banner_type: 'info',
    priority: 0, start_date: '', end_date: '', is_active: true
  });

  // ---- Bot ----
  const fetchBotPhrases = async () => {
    setLoadingPhrases(true);
    try {
      const res = await botApi.getPhrases();
      setBotPhrases(res.data || []);
    } catch (error) { console.error('Error fetching bot phrases:', error); }
    finally { setLoadingPhrases(false); }
  };

  const handleSeedBotPhrases = async () => {
    try {
      const res = await botApi.seedPhrases();
      toast.success(res.data.message);
      fetchBotPhrases();
    } catch (error) { toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error')); }
  };

  const handleAddPhrase = async () => {
    if (!newPhrase.phrase.trim()) { toast.error(isItalian ? 'Inserisci una frase' : 'Enter a phrase'); return; }
    try {
      await botApi.createPhrase(newPhrase);
      toast.success(isItalian ? 'Frase aggiunta!' : 'Phrase added!');
      setShowAddPhraseDialog(false);
      setNewPhrase({ command: '!ciao', phrase: '' });
      fetchBotPhrases();
    } catch (error) { toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error')); }
  };

  const handleDeletePhrase = async (phraseId) => {
    try {
      await botApi.deletePhrase(phraseId);
      toast.success(isItalian ? 'Frase eliminata!' : 'Phrase deleted!');
      setBotPhrases(prev => prev.filter(p => p.phrase_id !== phraseId));
    } catch (error) { toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error')); }
  };

  // ---- Logs ----
  const fetchActivityLogs = async (filterAction = null) => {
    setLoadingLogs(true);
    try {
      const params = { limit: 200 };
      if (filterAction && filterAction !== 'all') params.action = filterAction;
      const [logsRes, actionsRes] = await Promise.all([logsApi.getLogs(params), logsApi.getActionTypes()]);
      setActivityLogs(logsRes.data || []);
      setActionTypes(actionsRes.data || []);
    } catch (error) { console.error('Error fetching logs:', error); }
    finally { setLoadingLogs(false); }
  };

  const handleClearOldLogs = async () => {
    if (!window.confirm(isItalian ? 'Eliminare i log più vecchi di 30 giorni?' : 'Delete logs older than 30 days?')) return;
    try {
      const res = await logsApi.clearOldLogs(30);
      toast.success(res.data.message);
      fetchActivityLogs(logFilter);
    } catch (error) { toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error')); }
  };

  const handleClearAllLogs = async () => {
    if (!window.confirm(isItalian ? 'ATTENZIONE: Eliminare TUTTI i log? Questa azione è irreversibile!' : 'WARNING: Delete ALL logs? This action is irreversible!')) return;
    try {
      const res = await logsApi.clearAllLogs();
      toast.success(res.data.message);
      setActivityLogs([]);
    } catch (error) { toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error')); }
  };

  // ---- Banners ----
  const fetchBanners = async () => {
    setLoadingBanners(true);
    try {
      const res = await bannersApi.getAll();
      setBanners(res.data || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error(isItalian ? 'Errore caricamento banner' : 'Error loading banners');
    } finally { setLoadingBanners(false); }
  };

  const handleOpenBannerDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setBannerForm({
        title: banner.title || '', message: banner.message || '', html_content: banner.html_content || '', link: banner.link || '',
        image_url: banner.image_url || '', banner_type: banner.banner_type || 'info',
        priority: banner.priority || 0,
        start_date: banner.start_date ? new Date(banner.start_date).toISOString().slice(0, 16) : '',
        end_date: banner.end_date ? new Date(banner.end_date).toISOString().slice(0, 16) : '',
        is_active: banner.is_active !== false
      });
    } else {
      setEditingBanner(null);
      setBannerForm({ title: '', message: '', html_content: '', link: '', image_url: '', banner_type: 'info', priority: 0, start_date: '', end_date: '', is_active: true });
    }
    setShowBannerDialog(true);
  };

  const handleSaveBanner = async () => {
    if (!bannerForm.title || !bannerForm.message) {
      toast.error(isItalian ? 'Titolo e messaggio obbligatori' : 'Title and message required'); return;
    }
    try {
      const data = {
        ...bannerForm,
        html_content: bannerForm.html_content || null,
        start_date: bannerForm.start_date ? new Date(bannerForm.start_date).toISOString() : null,
        end_date: bannerForm.end_date ? new Date(bannerForm.end_date).toISOString() : null,
        link: bannerForm.link || null,
        image_url: bannerForm.image_url || null
      };
      if (editingBanner) {
        await bannersApi.update(editingBanner.banner_id, data);
        toast.success(isItalian ? 'Banner aggiornato!' : 'Banner updated!');
      } else {
        await bannersApi.create(data);
        toast.success(isItalian ? 'Banner creato!' : 'Banner created!');
      }
      setShowBannerDialog(false);
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleToggleBanner = async (banner) => {
    try {
      await bannersApi.toggle(banner.banner_id);
      setBanners(prev => prev.map(b => b.banner_id === banner.banner_id ? { ...b, is_active: !b.is_active } : b));
      toast.success(banner.is_active
        ? (isItalian ? 'Banner disattivato' : 'Banner deactivated')
        : (isItalian ? 'Banner attivato' : 'Banner activated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm(isItalian ? 'Eliminare questo banner?' : 'Delete this banner?')) return;
    try {
      await bannersApi.delete(bannerId);
      setBanners(prev => prev.filter(b => b.banner_id !== bannerId));
      toast.success(isItalian ? 'Banner eliminato!' : 'Banner deleted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  return {
    // Bot
    botPhrases, loadingPhrases, fetchBotPhrases,
    handleDeletePhrase, handleSeedBotPhrases,
    showAddPhraseDialog, setShowAddPhraseDialog, newPhrase, setNewPhrase, handleAddPhrase,

    // Logs
    activityLogs, loadingLogs, logFilter, setLogFilter, actionTypes,
    fetchActivityLogs, handleClearOldLogs, handleClearAllLogs,

    // Banners
    banners, loadingBanners, fetchBanners,
    handleOpenBannerDialog, handleToggleBanner, handleDeleteBanner,
    showBannerDialog, setShowBannerDialog, editingBanner, setEditingBanner,
    bannerForm, setBannerForm, handleSaveBanner,
  };
};
